import {
  SET_LOGO_PRESET_PROPERTY,
  SET_PALETTE_HEX,
  SET_SVG,
  SET_LOGO,
  SET_SLOGAN_ALIGNMENT,
  SET_WORDMARK_ALIGNMENT,
  SET_ICON_ORIENTATION,
  SET_WORDMARK_MULTILINE,
  SET_SLOGAN_MULTILINE,
  SET_WORDMARK_MULTICOLOR,
  SET_SLOGAN_MULTICOLOR,
  SET_TEMPLATE_ALIGNMENT,
  SET_SHAPE,
  SET_WORDMARK_SCRIPT,
  SET_MASK_STROKE,
  SET_ICON_SCALE,
  SET_MONOGRAM_TEXT,
} from './editor-mutations'

import { ResourceNamesEnum } from '@/services/api/api-service'
import getSlug from '@/lib/helpers/content/get-slug'
import { DATA_PALETTE_PATTERN } from '../../util'
import loader from '@/lib/helpers/content/loader'
import { getPageOfArray } from './common'
import { DEFAULT_SLOGAN_SCALE } from '@/lib/constants/editor'
import { getPaletteHexValue } from './logos'

/**
 * Builds template preview request body from logo state, then replaces template IDs in preset with a page of template ids of size batch_size
 * if template_previews_length === templates.length, does nothing
 * @param {string} wordmark wordmark of logo
 * @param {string} slogan slogan of logo
 * @param {Array} templates template array from which previews is generated
 * @param {number} batch_size number of templates to grab per call
 * @param {Object} editor_state the editor.js vuex store module
 */
const buildTemplatePreset = (
  wordmark,
  slogan,
  templates,
  template_previews_length,
  batch_size,
  editor_state
) => {
  if (template_previews_length === templates.lenth) return

  const preset = buildPresetFromLogoState(
    editor_state,
    true,
    false,
    wordmark,
    slogan
  )

  const template_ids = getPageOfArray(
    templates,
    template_previews_length,
    batch_size
  ).map(template => template.id)

  preset.presets.templates.ids = template_ids
  preset.quantity = template_ids.length || 1

  preset.wordmark_multicolor = 'default'
  preset.slogan_multicolor = 'default'
  preset.wordmark_multiline = 'default'
  preset.slogan_multiline = 'default'
  preset.shape = 'default'

  return preset
}

const getColorsUsedInSvg = (svg, commit) => {
  const used_colors = svg
    // Extracts all matches into an array
    .match(DATA_PALETTE_PATTERN)
    // Format matches so that only the string inside ".." is in the array
    .map(match =>
      match
        .replace(/"/g, '')
        .split('=')
        .pop()
    )
    // Filter out transparent elements
    .filter(color => color !== 'transparent')
    // Create a new Set containing all
    .reduce((colors, color) => colors.add(color), new Set())

  commit('SET_PALETTE_COLORS', ['background', ...Array.from(used_colors)])
}

const setCurrentLogo = ({ commit, dispatch }, { logo, params = {} }) => {
  getColorsUsedInSvg(logo.svg, commit)

  dispatch('setLogoId', logo.id)

  commit(SET_PALETTE_HEX, logo.assets.palette)

  // template, wordmark and slogan have slightly different json structure
  commit(SET_LOGO_PRESET_PROPERTY, {
    id: logo.template,
    resource: ResourceNamesEnum.Templates,
  })

  commit(SET_LOGO_PRESET_PROPERTY, {
    id: logo.wordmark,
    resource: ResourceNamesEnum.Wordmark,
  })

  commit(SET_LOGO_PRESET_PROPERTY, {
    id: logo.slogan,
    resource: ResourceNamesEnum.Slogan,
  })

  const { template } = logo.assets
  if (template) {
    commit(SET_WORDMARK_ALIGNMENT, template.wordmark_alignment || 'default')
    commit(SET_SLOGAN_ALIGNMENT, template.slogan_alignment || 'default')
    commit(SET_ICON_ORIENTATION, template.orientation)
    commit(SET_TEMPLATE_ALIGNMENT, template.template_alignment || 'default')

    if (template.hasOwnProperty('wordmark_multiline')) {
      commit(SET_WORDMARK_MULTILINE, template.wordmark_multiline)
    }
    if (template.hasOwnProperty('slogan_multiline')) {
      commit(SET_SLOGAN_MULTILINE, template.slogan_multiline)
    }
    if (template.hasOwnProperty('wordmark_multicolor')) {
      commit(SET_WORDMARK_MULTICOLOR, template.wordmark_multicolor)
    }
    if (template.hasOwnProperty('slogan_multicolor')) {
      commit(SET_SLOGAN_MULTICOLOR, template.slogan_multicolor)
    }
    if (template.hasOwnProperty('shape')) {
      commit(SET_SHAPE, template.shape)
    }
    if (template.hasOwnProperty('wordmark_script')) {
      commit(SET_WORDMARK_SCRIPT, template.wordmark_script)
    } else {
      commit(SET_WORDMARK_SCRIPT, false)
    }
    if (template.hasOwnProperty('stroke_mask_width')) {
      commit(SET_MASK_STROKE, template.stroke_mask_width)
    }
    if (template.hasOwnProperty('allowed_icon_sizes')) {
      commit(SET_ICON_SCALE, template.icon[0].scale)
    }
    if (template.hasOwnProperty('monogram_text')) {
      commit(SET_MONOGRAM_TEXT, template.monogram_text)
    }
  }

  const commitResource = (data, resource) => {
    if (!data) {
      commit(SET_LOGO_PRESET_PROPERTY, {
        id: null,
        resource,
      })
    } else {
      commit(SET_LOGO_PRESET_PROPERTY, {
        id: data.id,
        resource,
      })
    }
  }
  const icons = logo.assets.icons[0] ?? logo.assets.icons.icon
  commitResource(logo.assets.palette, ResourceNamesEnum.Palettes)
  commitResource(icons, ResourceNamesEnum.Icons)
  if (logo.assets.slogan_fonts) {
    commitResource(logo.assets.slogan_fonts[0], ResourceNamesEnum.Slogan_fonts)
  }
  commitResource(
    logo.assets.wordmark_fonts[0],
    ResourceNamesEnum.Wordmark_fonts
  )
  commit(SET_SVG, logo.svg)
  commit(SET_LOGO, logo)
}

const defaultIfUndefined = (val, defaultString = undefined) =>
  val === undefined ? defaultString ?? 'default' : val

const buildPresetFromLogoState = (
  state,
  disable_text_casing = true,
  disable_preference_mode = true,
  wordmark,
  slogan,
  icon_scale = 1,
  icon_horizontal_offset = 0,
  icon_vertical_offset = 0,
  slogan_alignment = 'default',
  icon_orientation = 'default',
  wordmark_alignment = 'default',
  design_alignment = undefined,
  slogan_scale = DEFAULT_SLOGAN_SCALE
) => {
  const payload = {
    wordmark,
    slogan,
    quantity: 1,
    disable_text_casing,
    disable_preference_mode,
    icon_scale,
    slogan_scale,
    icon_horizontal_offset,
    icon_vertical_offset,
    slogan_alignment,
    wordmark_alignment,
    icon_orientation,
    wordmark_multicolor: defaultIfUndefined(state.wordmark_multicolor),
    wordmark_multiline: defaultIfUndefined(state.wordmark_multiline),
    slogan_multicolor: defaultIfUndefined(state.slogan_multicolor),
    slogan_multiline: defaultIfUndefined(state.slogan_multiline),
    template_alignment: defaultIfUndefined(state.template_alignment),
    shape: defaultIfUndefined(state.shape),
    design_alignment,
    presets: {},
  }

  const resources_array = [
    ResourceNamesEnum.Icons,
    ResourceNamesEnum.Slogan_fonts,
    ResourceNamesEnum.Wordmark_fonts,
    ResourceNamesEnum.Templates,
    ResourceNamesEnum.Palettes,
  ]

  resources_array.forEach(res => {
    if (!state.logo_preset[res] || state.logo_preset[res].length === 0) {
      payload.presets[res] = {
        ids: [],
      }
    } else {
      payload.presets[res] = {
        ids: [state.logo_preset[res]],
      }
    }
  })

  // Overwrite the palettes when a custom ID is given
  if (state.logo_preset[ResourceNamesEnum.Palettes] === 'CUSTOM') {
    payload.presets[ResourceNamesEnum.Palettes] = {
      hex: state.logo_preset.palette_hex,
      ids: [],
    }
  }

  return payload
}

const buildUrl = (base, ...suffix) => {
  suffix.unshift(base)
  return suffix.join('/')
}

// root should be a div with display:none
const reColorLogoSvg = (root, recolor_palette, svg, old_palette = null) => {
  const new_palette = { ...recolor_palette }
  if (!new_palette.tertiary) {
    new_palette.tertiary = new_palette.primary
  }

  if (!new_palette.quaternary) {
    new_palette.quaternary = new_palette.background
  }
  const group = root.group()
  let svg_string = svg
  if (old_palette) {
    svg_string = svg_string.replace(old_palette.accent, new_palette.accent)
  }
  // add the SVG and select the logo
  group.svg(svg_string)
  const logo = group.children()[0]

  // Set up variables needed while looping,
  // since we don't have access to the Nuxt "this"
  const palette_keys = Object.keys(new_palette)
  const background = logo.data('background-color')

  logo.each(function() {
    // A mask does not need any processing
    if (this.parent().type === 'mask') {
      return
    }

    // remove style classes to make sure that global css from other logos can't affect this one
    const fill_css_classes = this.classes().filter(c => c.startsWith('fil'))
    fill_css_classes.forEach(c => this.removeClass(c))

    // The rect inserted likely has no data-*, so we can
    // locate it by comparing elements colors to the BG color
    if (this.attr('fill') === background) {
      this.attr({ fill: new_palette.background })
    }

    // Get the fill and stroke key to the palette
    // This can be transparent, which will be ignored
    const stroke = this.data('stroke-palette-color')
    const fill = this.data('fill-palette-color')

    // Do the color replacement if the stroke or fill exists in
    // our palette.
    if (palette_keys.find(key => key === stroke)) {
      this.attr({ stroke: new_palette[stroke] })
    }
    if (palette_keys.find(key => key === fill)) {
      this.attr({ fill: new_palette[fill] })
    }
  }, true)

  // replace the background color data-* in case we need to use this logo in the future
  logo.data({ 'background-color': new_palette.background })
  // Export the sSVG to string format that can be used by our Overlays
  const _svg = logo.svg()
  // Get rid of the excess HTML generated
  root.remove()

  return _svg
}

/**
 * Import our nav and convert the file path into a slug.
 *
 * @returns {promise}
 */
const nav = async () => {
  const { items } = await loader(['editor/nav'])

  return items.map(item => ({ ...item, slug: getSlug(item.slug) }))
}
/**
 * Builds an Array of palette names. slugs, and hexes
 *
 * @param {Object} logoPalette the palette Object from a Logo
 * @param {Array} limit an optional Array of colors that should be included in the map
 * @param {Array} custom an optional Array of extra map properties, to map custom palette properties (business card blank sides)
 */
const getMappedPalette = (logoPalette, limit = null, custom = []) => {
  const map = [
    { slug: 'primary', label: 'Business Name' },
    { slug: 'secondary', label: 'Slogan' },
    { slug: 'accent', label: 'Icon' },
    { slug: 'background', label: 'Background' },
    { slug: 'tertiary', label: 'Background Elements' },
    { slug: 'quaternary', label: 'Foreground Elements' },
  ]

  // Map colors to names for the User
  return [...map, ...custom].reduce((palette, item) => {
    // Grab our hex value from the palette
    const hex = getPaletteHexValue(logoPalette[item.slug])

    // If our logo has limit, check if it includes this colour
    if (limit && !limit.includes(item.slug)) {
      return palette
    }

    // Include the hex value to simplify previews
    if (hex) {
      palette.push({ hex, ...item })
    }

    return palette
  }, [])
}

const getMappedPalettefromLogo = logo => {
  const colors = logo.palette_colors || null

  return getMappedPalette(getPaletteFromLogo(logo), colors)
}

const getPaletteFromLogo = logo => logo?.palette_hex || logo.assets?.palette

export {
  getMappedPalettefromLogo,
  buildUrl,
  buildPresetFromLogoState,
  setCurrentLogo,
  reColorLogoSvg,
  buildTemplatePreset,
  getPaletteFromLogo,
  getMappedPalette,
  nav,
}
